import { useState } from 'react';
import { BUSINESS_INFO, SERVICES } from '../../mocks/lisaMerkleData';

const FORM_URL = 'https://readdy.ai/api/form/d71fh1plum2co2q1fn30';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const message = formData.get('message') as string;
    if (message && message.length > 500) return;

    setStatus('sending');
    try {
      const params = new URLSearchParams();
      formData.forEach((val, key) => params.append(key, val as string));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-cream-warm py-28">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-0 border border-stone/20">
          {/* Left: Info panel */}
          <div className="p-12 lg:p-16 bg-charcoal flex flex-col justify-between">
            <div>
              <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Get in Touch</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-cream mt-4 mb-6 leading-tight">
                Begin Your<br />
                <em className="not-italic text-stone">Design Experience</em>
              </h2>
              <p className="text-cream/50 text-sm leading-relaxed font-sans mb-10 max-w-xs">
                Reach out to discuss your project. We work with discerning clients in Beverly Hills, Los Angeles, and nationwide.
              </p>
            </div>

            <ul className="flex flex-col gap-6">
              <li>
                <a href={BUSINESS_INFO.phoneHref} className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center border border-stone/25 group-hover:bg-stone/10 group-hover:border-stone/50 transition-all flex-shrink-0">
                    <i className="ri-phone-line text-stone text-lg" />
                  </div>
                  <div>
                    <p className="text-cream/35 text-[10px] tracking-[0.2em] uppercase font-sans">Phone — Click to Call</p>
                    <p className="text-cream font-sans font-medium text-base mt-0.5 tracking-wide">{BUSINESS_INFO.phone}</p>
                  </div>
                </a>
              </li>
              <li>
                <a href={`mailto:${BUSINESS_INFO.email}`} className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center border border-stone/25 group-hover:bg-stone/10 group-hover:border-stone/50 transition-all flex-shrink-0">
                    <i className="ri-mail-line text-stone text-lg" />
                  </div>
                  <div>
                    <p className="text-cream/35 text-[10px] tracking-[0.2em] uppercase font-sans">Email</p>
                    <p className="text-cream font-sans font-medium text-sm mt-0.5">{BUSINESS_INFO.email}</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-stone/25 flex-shrink-0">
                    <i className="ri-map-pin-line text-stone text-lg" />
                  </div>
                  <div>
                    <p className="text-cream/35 text-[10px] tracking-[0.2em] uppercase font-sans">Studio Address</p>
                    <p className="text-cream font-sans font-medium text-sm mt-0.5">{BUSINESS_INFO.address}</p>
                    <p className="text-cream/50 font-sans text-xs">{BUSINESS_INFO.city}</p>
                  </div>
                </div>
              </li>
            </ul>

            <div className="mt-10 pt-8 border-t border-cream/8 flex items-center gap-4">
              <a
                href={BUSINESS_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-cream/15 text-cream/40 hover:text-stone hover:border-stone/40 transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-sm" />
              </a>
              <a
                href={BUSINESS_INFO.facebook}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-cream/15 text-cream/40 hover:text-stone hover:border-stone/40 transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm" />
              </a>
              <span className="text-cream/20 text-[10px] font-sans tracking-wider">@lisamerkledesign</span>
            </div>
          </div>

          {/* Right: Form */}
          <div className="p-12 lg:p-16 bg-white">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center border border-stone/40 mb-6">
                  <i className="ri-check-line text-stone-dark text-2xl" />
                </div>
                <h3 className="font-display text-3xl text-charcoal mb-3">Thank You</h3>
                <p className="text-charcoal/55 text-sm font-sans max-w-xs leading-relaxed">
                  We've received your inquiry and will be in touch within 24 hours.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-[10px] tracking-wider uppercase font-sans text-stone-dark cursor-pointer hover:underline"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form method="POST" className="flex flex-col gap-5 h-full">
                <div className="mb-2">
                  <h3 className="font-display text-2xl font-light text-charcoal">Request a Consultation</h3>
                  <p className="text-charcoal/40 text-xs font-sans mt-1 tracking-wide">We respond within 24 hours.</p>
                </div>

                <div>
                  <label className="block text-charcoal/45 text-[10px] tracking-[0.2em] uppercase font-sans mb-1.5">Full Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-cream-warm border border-stone/20 text-charcoal text-sm font-sans focus:outline-none focus:border-stone-dark placeholder:text-charcoal/25 transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-charcoal/45 text-[10px] tracking-[0.2em] uppercase font-sans mb-1.5">Phone *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(323) 000-0000"
                      className="w-full px-4 py-3 bg-cream-warm border border-stone/20 text-charcoal text-sm font-sans focus:outline-none focus:border-stone-dark placeholder:text-charcoal/25 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-charcoal/45 text-[10px] tracking-[0.2em] uppercase font-sans mb-1.5">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-cream-warm border border-stone/20 text-charcoal text-sm font-sans focus:outline-none focus:border-stone-dark placeholder:text-charcoal/25 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-charcoal/45 text-[10px] tracking-[0.2em] uppercase font-sans mb-1.5">Service Interest *</label>
                  <div className="relative">
                    <select
                      name="service"
                      required
                      className="w-full px-4 py-3 bg-cream-warm border border-stone/20 text-charcoal text-sm font-sans focus:outline-none focus:border-stone-dark appearance-none cursor-pointer pr-10"
                    >
                      <option value="">Select a service...</option>
                      {SERVICES.map((s) => (
                        <option key={s.title} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-charcoal/30 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-charcoal/45 text-[10px] tracking-[0.2em] uppercase font-sans mb-1.5">
                    Project Details
                    <span className="normal-case ml-1 text-charcoal/25">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Tell us about your space, timeline, and vision..."
                    className="w-full px-4 py-3 bg-cream-warm border border-stone/20 text-charcoal text-sm font-sans focus:outline-none focus:border-stone-dark placeholder:text-charcoal/25 transition-colors resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-700 text-xs font-sans">Something went wrong. Please try again or call us directly at {BUSINESS_INFO.phone}.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-4 bg-charcoal text-cream text-[10px] tracking-[0.2em] uppercase font-medium font-sans hover:bg-charcoal/85 transition-all duration-300 cursor-pointer disabled:opacity-50 whitespace-nowrap mt-1"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Inquiry — Begin Your Project'}
                </button>

                <p className="text-charcoal/30 text-[10px] font-sans text-center leading-relaxed">
                  Or call directly: <a href={BUSINESS_INFO.phoneHref} className="text-stone-dark hover:underline cursor-pointer">{BUSINESS_INFO.phone}</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
